import React, {useCallback, useContext, useMemo, useState} from "react";
import {UserHomepageControllerContext, UserHomepageStateContext} from "../../../../index";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Utils from "../../../../../../../core/services/utils/utils";
import {ReactSortable} from "react-sortablejs";
import UserHomepageControlPanelComponent from "../component";
import {UserHomepageEventEmitter} from "../../../../models";
import Api from "../../../../../../../core/services/api/api";
import useCanManageHomepage from "../../../../hooks/use-can-manage-homepage";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import TryAgain from "../../../../../../components/app-specific/try-again";

export const UserHomepageControlPanelComponents = ({tab, dragging: draggingTabs}) => {
    const [reordering, setReordering] = useState(false);
    const [draggingComponents, setDraggingComponents] = useState(false);
    const state = useContext(UserHomepageStateContext);
    const controller = useContext(UserHomepageControllerContext)
    const isMounted = useIsMounted();
    const canManageHomepage = useCanManageHomepage();
    const {packages} = useAccessLevels();
    const hasHomepageManageFlag =
        packages.clientPlatform.manageWebHomepageEditions ||
        packages.clientPlatform.managePhoneHomepageEditions;

    const loading = useMemo(() => tab?.loading, [tab])
    /**@type {UserHomepageComponent[]}*/
    const components = useMemo(() => tab?.components ?? [], [tab])
    const dragging = useMemo(() => draggingComponents || draggingTabs, [draggingTabs, draggingComponents]);

    /**
     * Persists a same-list component reorder using Sortable's old/new indices so the
     * moved item is identified correctly whether it was dragged up or down.
     * @param {import('sortablejs').SortableEvent} event
     */
    const reorderComponents = useCallback(async (event) => {
        if (!canManageHomepage || !hasHomepageManageFlag) return;
        const oldIndex = event.oldDraggableIndex;
        const newIndex = event.newDraggableIndex;
        if (
            typeof oldIndex !== 'number' ||
            typeof newIndex !== 'number' ||
            oldIndex === newIndex ||
            oldIndex < 0 ||
            newIndex < 0 ||
            oldIndex >= components.length ||
            newIndex >= components.length
        ) {
            return;
        }

        const previousComponents = components;
        const movedComponent = previousComponents[oldIndex];
        controller.reorderComponents(
            tab.id,
            Utils.reorderList(previousComponents, oldIndex, newIndex),
            UserHomepageEventEmitter.controlPanel,
            state
        );

        setReordering(true);
        const response = await Api.updateHomepageEditionTabComponentOrderIndex({
            id: movedComponent.id,
            index: newIndex + 1,
            tabId: tab.id,
        });
        if (!isMounted()) return;
        setReordering(false);
        if (!response?.resultFlag) {
            controller.reorderComponents(
                tab.id,
                previousComponents,
                UserHomepageEventEmitter.controlPanel,
                state
            );
        }
    }, [canManageHomepage, components, controller, hasHomepageManageFlag, isMounted, state, tab.id]);

    const onDragStart = useCallback(() => setDraggingComponents(true), []);
    const onDragEnd = useCallback(() => setDraggingComponents(false), []);

    const content = useMemo(() => (
        <ReactSortable
            className={'user-homepage-control-panel-components'}
            list={components}
            setList={Utils.noop}
            onUpdate={reorderComponents}
            onStart={onDragStart}
            onEnd={onDragEnd}
            handle={'.handle'}
            group={'components'}
            animation={210}
            forceFallback={false}
            swapThreshold={0.65}
            ghostClass={"dragged-component"}
        >
            {
                components.map(component => (
                    <UserHomepageControlPanelComponent
                        key={component.id}
                        tabId={tab.id}
                        component={component}
                        sortable={!reordering}
                        dragging={dragging}
                    />
                ))
            }
        </ReactSortable>
    ), [components, dragging, onDragEnd, onDragStart, reorderComponents, reordering, tab.id])

    return (
        <>
            {
                loading &&
                Array(5).fill(null).map((_, e) => (
                    <div key={e} className={'loading control-panel component'}>
                        <div/>
                    </div>
                ))
            }
            {
                !loading && (
                    !components?.length
                        ? <TryAgain
                            text={'This tab does not have any components.'}
                        />
                        : content
                )
            }
        </>
    );
}

export default UserHomepageControlPanelComponents;
